// Token-level identifier walk over a Monaco buffer, shared by every rename
// surface that reads a buffer rather than the catalog:
//
//   • buffer-local rename (alias / CTE)   → `bufferRenamePlan.ts`
//   • open-tab rewrite after a DB rename  → `tabRewritePlanner.ts`
//   • the cheap open-tab candidate scan   → `renameTabScanner.ts`
//
// NAV10 — no regex identifier matching. Every candidate here is a real
// identifier chain read by the resolver's `readQualifiedName`, with comments,
// string literals and PG dollar-tagged blocks skipped wholesale via the shared
// `trySkipNonCode` primitives. An alias `o` therefore never matches the `o`
// inside `orders` or inside `'…o…'`.
//
// Extracted from the walk `renameTabScanner.scanSingleTab` already ran (which
// now calls this module) so the three consumers share ONE scanner instead of
// each growing their own.

import type { IdentifierQuoting, ParseOptions, QualifiedName } from '../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../sql-resolver/classify/tokenSkip';
import { foldIdentifier, readIdentifier, readQualifiedName } from '../../sql-resolver/scope/identifiers';

/** One dotted identifier chain (`name`, `schema.name`, `db.schema.name`, …). */
export type IdentifierChain = {
  /** Byte range of the whole chain. */
  readonly start: number;
  readonly end: number;
  /** Byte range + text of the FIRST part (`u` in `u.email`). */
  readonly head: {
    readonly start: number;
    readonly end: number;
    readonly value: string;
    readonly quoted: boolean;
    readonly quoting: IdentifierQuoting;
  };
  /** Byte range of the LAST part, delimiters included (`[email]` in `[u].[email]`). */
  readonly terminal: { readonly start: number; readonly end: number };
  readonly terminalQuoting: IdentifierQuoting;
  readonly qualified: QualifiedName;
  readonly partCount: number;
};

/** Bytes that can never start an identifier chain — skipped one at a time so
 *  the (comparatively expensive) `readQualifiedName` probe is not attempted on
 *  every operator character. Mirrors `renameTabScanner`'s SKIP_BYTES. */
const SKIP_BYTES: ReadonlySet<string> = new Set([
  ' ', '\t', '\n', '\r',
  ',', ';', '=', '+', '-', '*', '/', '%',
  '<', '>', '!', '?', '|', '&', '~', '^', '@',
  '(', ')',
]);

/** Walk `source[start, end)` and invoke `onChain` once per identifier chain.
 *  Returning `false` from `onChain` stops the walk. */
export function forEachIdentifierChain(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
  onChain: (chain: IdentifierChain) => void | boolean,
): void {
  // Disable backtick / bracket auto-skip so quoted identifiers stay reachable
  // by `readQualifiedName`. Comments, single-quoted strings and PG dollar tags
  // still get skipped via `trySkipNonCode`.
  const skipCtx = {
    ...skipContextFor(options),
    supportsBacktick: false,
    supportsBracket: false,
  };
  // MySQL has no double-quoted identifiers by default (`"…"` is a string), so
  // there the double quote must stay a skippable literal.
  const treatDoubleQuoteAsString = options.engine === 'mysql';
  let i = Math.max(0, start);
  const limit = Math.min(end, source.length);

  while (i < limit) {
    const c = source[i];
    // `trySkipNonCode` BEFORE the cheap-byte skip so `--`, `/*`, `'…'`, `#…`
    // and `$tag$ … $tag$` regions are skipped wholesale — otherwise the
    // leading byte gets nibbled by the cheap-byte branch and the comment
    // body's identifiers surface as matches.
    if (c !== '"' || treatDoubleQuoteAsString) {
      const skipped = trySkipNonCode(source, i, limit, skipCtx);
      if (skipped >= 0 && skipped > i) { i = skipped; continue; }
    }
    if (SKIP_BYTES.has(c)) { i += 1; continue; }
    const chain = readChain(source, i, limit, options);
    if (!chain) { i += 1; continue; }
    if (onChain(chain) === false) return;
    i = chain.end > i ? chain.end : i + 1;
  }
}

function readChain(
  source: string,
  i: number,
  limit: number,
  options: ParseOptions,
): IdentifierChain | null {
  const qn = readQualifiedName(source, i, limit, options);
  if (!qn) return null;
  const head = readIdentifier(source, qn.range.startOffset, limit, options);
  if (!head) return null;
  const terminalHead = readIdentifier(source, qn.terminalRange.startOffset, limit, options);
  return {
    start: qn.range.startOffset,
    end: qn.range.endOffset,
    head: {
      start: head.start,
      end: head.end,
      value: head.value,
      quoted: head.quoted,
      quoting: head.quoting,
    },
    terminal: {
      start: qn.terminalRange.startOffset,
      end: qn.terminalRange.endOffset,
    },
    terminalQuoting: terminalHead?.quoting ?? 'unquoted',
    qualified: qn.qualified,
    partCount: qn.partCount,
  };
}

/**
 * The RELATION a chain names in its QUALIFIER position — the part before the
 * terminal (`users` in `users.email`, `orders` in `dbo.orders.total`). It is
 * neither the head nor the terminal at three parts, so the chain carries no
 * per-part token for it and every caller has to synthesize one.
 *
 * Both rename arms did, and both kept only the FOLDED halves. A `QualifiedName`
 * whose spelled component is absent is not neutral: `identityCaseContext.ts`
 * falls back to the folded value, so the two engines that compare the SPELLED
 * halves — SQL Server on a case-sensitive catalog collation, and MySQL with
 * `@@lower_case_table_names = 0`, where a schema is a directory on disk —
 * compared a folded `sales` against the host's `Sales` and refused the correct
 * host (or, with the cases the other way round, accepted a different one). The
 * chain has both halves of both components; this hands over both, so no caller
 * is in a position to drop one.
 *
 * The qualifier's own DATABASE is the third component, and at FOUR parts it is
 * not in `chain.qualified` at all: `readQualifiedName` keeps the LAST THREE
 * components, so `B.dbo.users.id` arrives as `dbo.users.id` with `B` surviving
 * only as the chain's HEAD. Dropping it was not neutral — the identity
 * comparator treats a component absent on one side as a WILDCARD, so
 * `B.dbo.users.id` matched a rename of `A.dbo.users.id` and the buffer was
 * rewritten to name a column of a table in a database nothing renamed. The head
 * IS that component here (`parts[0]`), spelled and — folded with this chain's
 * own `ParseOptions` — comparable, so it is handed over with the rest.
 *
 * `null` when the chain has no qualifier at all (a bare token) — callers treat
 * that as "the qualifier cannot be read", which is what they did with an empty
 * `foldedSchema` before — and equally when the chain is too long to read one:
 * at FIVE parts (`server.db.schema.table.column`) the head is the SERVER and
 * the database is the part `readQualifiedName` discards, so nothing in the
 * chain says which database the qualifier lives in. Unreadable is not
 * unqualified; a caller that guessed there would be guessing about a linked
 * server.
 */
export function qualifierRelationOf(
  chain: IdentifierChain,
  options: ParseOptions,
): QualifiedName | null {
  const foldedName = chain.qualified.foldedSchema;
  if (!foldedName) return null;
  if (chain.partCount >= 5) return null;
  const name = chain.qualified.schema ?? '';
  // Four parts: `db.schema.relation.leaf`. The three qualified slots already
  // hold `schema.relation.leaf`, so the database is the head and nothing else.
  const database = chain.partCount === 4 ? chain.head.value : undefined;
  return {
    name,
    raw: name,
    // Chain-wide (`parts.some(p => p.quoted)`), NOT this component's own — the
    // chain carries per-part quoting for its head and its terminal only, and a
    // qualifier at three parts is neither. Inert because every comparator over
    // this identity reads `name` / `schema` / `folded*` and never `quoted`, and
    // `foldedName` is already the fold `readQualifiedName` computed from the
    // qualifier part's OWN flag. Do not fold with it.
    quoted: chain.qualified.quoted,
    foldedName,
    // The qualifier's own schema: at three parts the chain's DATABASE slot
    // holds it (`dbo` in `dbo.orders.total`). Spelled and folded together.
    schema: chain.qualified.database,
    foldedSchema: chain.qualified.foldedDatabase,
    // Both halves again — `identityCaseContext.databaseComponent` reads the
    // FOLDED one to decide the component is present at all, so a spelled
    // database with no fold would be silently ignored and the wildcard match
    // would come straight back.
    ...(database === undefined
      ? {}
      : { database, foldedDatabase: foldIdentifier(database, chain.head.quoted, options) }),
  };
}

/** Render a name the user TYPED for a token that was written with `quoting` in
 *  the source. A quoted occurrence stays quoted in the same style — replacing
 *  `[email]` with a bare `email_address` would silently change the meaning of a
 *  name that needed delimiters (reserved word, embedded space, case-sensitive
 *  engine).
 *
 *  TYPED is the whole contract, and the reason the shortcut below is safe here
 *  and nowhere else: the buffer-local rename (`bufferRenamePlan.ts`, the only
 *  caller) hands over Monaco's raw F2 text, so `[x]` means "the user typed the
 *  delimiters" and wrapping them again would make `[[x]]`. A name that has
 *  already been DECODED must not come through here — its bytes may themselves
 *  start with a delimiter character (a PostgreSQL column literally named
 *  `"next"`), and the shortcut would re-read those bytes as delimiters and emit
 *  a different identifier. Decoded bytes go through `dialect.quoting.ts`'s
 *  `quoteWithStyle`, which escapes them; see `terminalTokenEdit.ts`'s
 *  `renderCatalogToken`. */
export function renderRenamedToken(newName: string, quoting: IdentifierQuoting): string {
  // The user may type an already-delimited name in the rename box — take it
  // verbatim rather than double-wrapping it into `[[x]]`.
  if (/^[["`]/.test(newName)) return newName;
  switch (quoting) {
    case 'bracket':
      return `[${newName.replace(/]/g, ']]')}]`;
    case 'double-quote':
      return `"${newName.replace(/"/g, '""')}"`;
    case 'backtick':
      return `\`${newName.replace(/`/g, '``')}\``;
    case 'unquoted':
    default:
      return newName;
  }
}
